import React, { useState, useEffect, useMemo, Fragment } from "react";
import { useFormikContext } from "formik";
import get from "lodash/get";
import { connect } from "react-redux";
import { fetchAppFonts } from "actions/app";
import { bindActionCreators } from "redux";
import { STATE_KEYS } from "utils/constants/state";
import random from "lodash/random";
import FontFamilySelector from "./FontFamilySelector";
import VisibilitySensor from "react-visibility-sensor";
import Button from "components/Form/fields/Button";
import { BUTTON_CLASS_CONTEXT, INITIAL_LOAD_COUNT } from "utils/constants/ui";
import ExternalLink from "components/ExternalLink";
import DebounceInput from "components/DebounceInput";
import { matchSorter } from "match-sorter";
import { DEFAULT_SYSTEM_FONT_FAMILY } from "utils/theme";
import classnames from "classnames";

const SkeletonFontFamilyRow = () => {
  const width = useMemo(() => random(5, 10) * 10, []);
  return (
    <div className="font-family--preview bb hairline-1 pa3 pointer">
      <div className={`shimmer h2 w-${width}`} />
    </div>
  );
};

const ThemeFontPicker = ({ fonts, actions }) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const currentFont = get(values, STATE_KEYS.THEME.FONT_FAMILY);
  const isTouched = get(touched, STATE_KEYS.THEME.FONT);

  const [fontsFetched, setFontsFetched] = useState(fonts.length > 0);
  const [search, setSearch] = useState("");

  const [loadCount, setLoadCount] = useState(INITIAL_LOAD_COUNT);
  const filtered = search
    ? matchSorter(fonts, search, {
        keys: ["family"]
      })
    : fonts;

  useEffect(() => {
    if (!fonts.length && !fontsFetched) {
      actions.fetchAppFonts(() => {
        setFontsFetched(true);
      });
    }
  }, []);

  let listContent;
  const noneMatchSearch = !filtered.length && search;
  if (fontsFetched) {
    if (filtered.length) {
      const disableMore = loadCount >= filtered.length;
      const searchNote = search
        ? `${filtered.length} matches`
        : `${disableMore ? filtered.length : loadCount} of ${filtered.length}`;

      listContent = (
        <Fragment>
          <ul className="list ma0 pa0">
            {filtered.slice(0, loadCount).map((font) => (
              <li key={font.family}>
                <VisibilitySensor>
                  {({ isVisible }) => (
                    <FontFamilySelector
                      key={font.family}
                      isVisible={isVisible}
                      {...font}
                    />
                  )}
                </VisibilitySensor>
              </li>
            ))}
          </ul>
          <div className="pa3 w-100 flex flex-row justify-between items-center">
            <div className="f6">{searchNote}</div>
            <Button
              theme="slim"
              copy="Load more"
              disabled={disableMore}
              classes={{
                contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
              }}
              onClick={() => {
                setLoadCount(loadCount + INITIAL_LOAD_COUNT);
              }}
            />
          </div>
        </Fragment>
      );
    } else if (noneMatchSearch) {
      listContent = (
        <div className="bb hairline-1 pa3 pointer">
          <div className="f5">No font matches your search</div>
        </div>
      );
    } else {
      listContent = (
        <div className="bb hairline-1 pa3 pointer">
          <div className="f5">No fonts available</div>
        </div>
      );
    }
  } else {
    listContent = new Array(INITIAL_LOAD_COUNT)
      .fill("")
      .map((_, skeletonIx) => <SkeletonFontFamilyRow key={skeletonIx} />);
  }
  const currentIsDefault = currentFont === DEFAULT_SYSTEM_FONT_FAMILY;

  return (
    <ul className="h-100 ma0 pa0 list bg-white bh--inset">
      <div className="ph3 pt3 w-100 dib">
        <DebounceInput
          placeholder="Search Google fonts"
          inputValue={search}
          valueSet={(updatedSearch) => {
            if (!updatedSearch) {
              setLoadCount(INITIAL_LOAD_COUNT);
            }
            setSearch(updatedSearch);
          }}
        />
        <div className="w-100 fr flex justify-end pt2 pb3">
          <ExternalLink copy="All fonts" href="https://fonts.google.com/" />
        </div>
      </div>
      <div className="pt3 bg-content--secondary bh--inset bt bb hairline-1 ph3">
        <div className="flex flex-row w-100 justify-between pb2">
          <div className="f6 fw5 self-center">Active font</div>
          <div className="f6 self-center">{currentFont}</div>
        </div>
        <div className="w-100 pb2">
          <span
            onClick={() => {
              if (!currentIsDefault) {
                setFieldValue(STATE_KEYS.THEME.FONT, {
                  family: DEFAULT_SYSTEM_FONT_FAMILY,
                  variants: []
                });
                if (!isTouched) {
                  setFieldTouched(STATE_KEYS.THEME.FONT, true);
                }
              }
            }}
            className={classnames("f7 f6-ns", {
              "link color-primary link__decoration--hover pointer fw6": !currentIsDefault,
              "black-50 cursor-not-allowed fw5": currentIsDefault
            })}
          >
            Reset
          </span>
        </div>
      </div>
      {listContent}
    </ul>
  );
};

const mapStateToProps = (state) => {
  return {
    fonts: state.getIn(["app", "data", "fonts"]).toJS()
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchAppFonts
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(ThemeFontPicker);
